/**
 *  Cleverbikes Predictive Search — V2
 *  Features:
 *  - Kategorie-Synonym-Mapping (JSON aus #cb-search-synonyms)
 *  - Collection→Product Bridge (API-Collection-Treffer → Produkte nachladen)
 *  - SKU-Normalisierung (B-15579 → auch 15579 und B15579)
 */
if (!customElements.get('search-form')) {
  class SearchForm extends HTMLElement {
    constructor() {
      super();
      this._synonyms = null;
    }

    connectedCallback() {
      this.container = document.getElementById('Search-Drawer');
      if (!this.container) return;

      this.form = this.container.querySelector('form');
      this.input = this.container.querySelector('input[type="search"]');
      this.predictiveSearchResults = this.container.querySelector('.search-drawer--content');
      if (!this.form || !this.input || !this.predictiveSearchResults) return;

      var defaultEl = document.getElementById('cb-search-default');
      this.defaultContentClone = defaultEl ? defaultEl.cloneNode(true) : null;

      this._loadSynonyms();
      this.setupEventListeners();
    }

    /* ── Synonym-Dictionary laden ── */
    _loadSynonyms() {
      try {
        var el = document.getElementById('cb-search-synonyms');
        if (el) {
          var data = JSON.parse(el.textContent);
          this._synonyms = data.categories || {};
        }
      } catch (e) {
        this._synonyms = {};
      }
    }

    /* ── Kategorie-Handles für Suchbegriff finden ── */
    _getCollectionHandles(term) {
      if (!this._synonyms) return [];
      var lower = term.toLowerCase().trim();
      // Exakter Match
      if (this._synonyms[lower]) return this._synonyms[lower];
      // Teilmatch: Prüfe ob der Suchbegriff in einem Synonym-Key enthalten ist
      var handles = [];
      for (var key in this._synonyms) {
        if (key.indexOf(lower) === 0 || lower.indexOf(key) === 0) {
          this._synonyms[key].forEach(function(h) {
            if (handles.indexOf(h) === -1) handles.push(h);
          });
        }
      }
      return handles;
    }

    /* ── SKU-Pattern erkennen ── */
    _isSkuQuery(term) {
      return /^[bB]?-?\d{4,}$/.test(term.trim());
    }

    /* ── SKU-Varianten erzeugen ── */
    _skuVariants(term) {
      var t = term.trim().toUpperCase();
      var variants = [t];
      var noDash = t.replace(/-/g, '');
      if (variants.indexOf(noDash) === -1) variants.push(noDash);
      var digits = t.replace(/[^0-9]/g, '');
      if (digits.length >= 4) {
        var withPrefix = 'B-' + digits;
        if (variants.indexOf(withPrefix) === -1) variants.push(withPrefix);
        var withoutPrefix = digits;
        if (variants.indexOf(withoutPrefix) === -1) variants.push(withoutPrefix);
      }
      return variants;
    }

    /* ── Default Content wiederherstellen ── */
    restoreDefaultContent() {
      if (!this.predictiveSearchResults) return;
      this.predictiveSearchResults.classList.remove('loading');
      if (this.defaultContentClone) {
        this.predictiveSearchResults.innerHTML = '';
        this.predictiveSearchResults.appendChild(this.defaultContentClone.cloneNode(true));
      } else {
        this.predictiveSearchResults.innerHTML = '';
      }
    }

    setupEventListeners() {
      this.form.addEventListener('submit', this.onFormSubmit.bind(this));
      this.input.addEventListener('input', debounce((event) => {
        this.onChange(event);
      }, 300).bind(this));
    }

    getQuery() {
      return this.input.value.trim();
    }

    onChange() {
      var searchTerm = this.getQuery();
      if (!searchTerm.length) {
        this.restoreDefaultContent();
        return;
      }
      this.predictiveSearchResults.classList.add('loading');
      this._performSearch(searchTerm);
    }

    onFormSubmit(event) {
      if (!this.getQuery().length) event.preventDefault();
    }

    /* ── Haupt-Suchlogik ── */
    async _performSearch(searchTerm) {
      try {
        // 1. Standard Predictive Search
        var mainHTML = await this._fetchPredictive(searchTerm);

        // 2. SKU-Fallback: Wenn SKU-Pattern und wenig/keine Ergebnisse
        if (this._isSkuQuery(searchTerm)) {
          var variants = this._skuVariants(searchTerm);
          var foundProducts = this._countProducts(mainHTML);

          if (foundProducts === 0) {
            for (var i = 0; i < variants.length; i++) {
              if (variants[i] === searchTerm.trim().toUpperCase()) continue;
              var fallbackHTML = await this._fetchPredictive(variants[i]);
              var fallbackCount = this._countProducts(fallbackHTML);
              if (fallbackCount > 0) {
                mainHTML = fallbackHTML;
                break;
              }
            }
          }
        }

        // 3. Collection→Product Bridge
        // Nur triggern wenn wenige direkte Produkttreffer (< 4) ODER Synonym-Match
        var directProductCount = this._countProducts(mainHTML);
        var synonymHandles = this._getCollectionHandles(searchTerm);
        var collectionHandles = this._extractCollectionHandles(mainHTML);

        var shouldBridge = synonymHandles.length > 0 || directProductCount < 4;

        if (shouldBridge) {
          var allHandles = collectionHandles.slice();
          synonymHandles.forEach(function(h) {
            if (allHandles.indexOf(h) === -1) allHandles.push(h);
          });

          if (allHandles.length > 0) {
            var extraProducts = await this._fetchCollectionProducts(allHandles);
            if (extraProducts.length > 0) {
              mainHTML = this._injectCollectionProducts(mainHTML, extraProducts, searchTerm);
            }
          }
        }

        // 4. Rendern
        this.renderSearchResults(mainHTML);
      } catch (e) {
        console.error('CB Search Error:', e);
        this.predictiveSearchResults.classList.remove('loading');
      }
    }

    /* ── Predictive Search API aufrufen ── */
    async _fetchPredictive(term) {
      var fields = 'title,product_type,vendor,tag,variants.title,variants.sku,body';
      var url = theme.routes.predictive_search_url
        + '?q=' + encodeURIComponent(term)
        + '&' + encodeURIComponent('resources[type]') + '=product,collection,article,query,page'
        + '&' + encodeURIComponent('resources[limit]') + '=10'
        + '&resources[options][fields]=' + encodeURIComponent(fields)
        + '&section_id=cb-predictive-search';

      var response = await fetch(url);
      if (!response.ok) throw new Error(response.status);
      var text = await response.text();
      var doc = new DOMParser().parseFromString(text, 'text/html');
      var sectionEl = doc.querySelector('#shopify-section-cb-predictive-search');
      return sectionEl ? sectionEl.innerHTML : '';
    }

    /* ── Produkt-Anzahl im HTML zählen ── */
    _countProducts(html) {
      var tmp = new DOMParser().parseFromString('<div>' + html + '</div>', 'text/html');
      var cards = tmp.querySelectorAll('.product-card, .cb-search-product');
      return cards.length;
    }

    /* ── Collection-Handles aus dem gerenderten HTML extrahieren ── */
    _extractCollectionHandles(html) {
      var handles = [];
      var tmp = new DOMParser().parseFromString('<div>' + html + '</div>', 'text/html');
      var links = tmp.querySelectorAll('.cb-search-cat-tile');
      links.forEach(function(a) {
        var href = a.getAttribute('href') || '';
        var match = href.match(/\/collections\/([a-z0-9_-]+)/i);
        if (match && match[1]) {
          handles.push(match[1].toLowerCase());
        }
      });
      return handles;
    }

    /* ── Produkte aus Collections laden ── */
    async _fetchCollectionProducts(handles) {
      var products = [];
      var seen = {};

      var toFetch = handles.slice(0, 3);

      var promises = toFetch.map(function(handle) {
        return fetch('/collections/' + handle + '?view=search-products')
          .then(function(r) { return r.ok ? r.text() : ''; })
          .catch(function() { return ''; });
      });

      var results = await Promise.all(promises);
      results.forEach(function(html) {
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var cards = doc.querySelectorAll('.cb-collection-product');
        cards.forEach(function(card) {
          var id = card.getAttribute('data-product-id');
          if (id && !seen[id]) {
            seen[id] = true;
            products.push(card.outerHTML);
          }
        });
      });

      return products;
    }

    /* ── Collection-Produkte in den Produkte-Tab injizieren ── */
    _injectCollectionProducts(html, extraProducts, searchTerm) {
      var tmp = new DOMParser().parseFromString('<div>' + html + '</div>', 'text/html');

      var panels = tmp.querySelectorAll('.side-panel-content--tab-panel');
      if (!panels.length) return html;

      var produktePanel = panels[0];
      var productGrid = produktePanel.querySelector('.search-drawer--products');

      // Sammle bestehende Produkt-URLs UND IDs um Duplikate zu vermeiden
      var existingIds = {};
      var existingUrls = {};
      if (productGrid) {
        productGrid.querySelectorAll('[data-product-id]').forEach(function(el) {
          existingIds[el.getAttribute('data-product-id')] = true;
        });
        productGrid.querySelectorAll('a[href*="/products/"]').forEach(function(a) {
          var href = a.getAttribute('href') || '';
          var match = href.match(/\/products\/([a-z0-9_-]+)/i);
          if (match && match[1]) {
            existingUrls[match[1].toLowerCase()] = true;
          }
        });
      }

      // Filtere Duplikate — prüfe gegen IDs UND URLs
      var newCards = [];
      extraProducts.forEach(function(cardHTML) {
        var idMatch = cardHTML.match(/data-product-id="([^"]+)"/);
        var urlMatch = cardHTML.match(/\/products\/([a-z0-9_-]+)/i);
        var isDupe = false;

        if (idMatch && existingIds[idMatch[1]]) isDupe = true;
        if (urlMatch && existingUrls[urlMatch[1].toLowerCase()]) isDupe = true;

        if (!isDupe) {
          newCards.push(cardHTML);
          if (idMatch) existingIds[idMatch[1]] = true;
          if (urlMatch) existingUrls[urlMatch[1].toLowerCase()] = true;
        }
      });

      if (newCards.length === 0) return html;

      if (productGrid) {
        newCards.forEach(function(cardHTML) {
          productGrid.insertAdjacentHTML('beforeend', cardHTML);
        });
      } else {
        var emptyMsg = produktePanel.querySelector('.cb-search-empty');
        if (emptyMsg) emptyMsg.remove();

        var newGrid = '<div class="search-drawer--products theme-scroll--inner">'
          + newCards.join('')
          + '</div>';

        var searchUrl = '/search?q=' + encodeURIComponent(searchTerm);
        var allLink = '<a href="' + searchUrl + '&type=product" class="cb-search-all-link">Alle Ergebnisse ansehen →</a>';

        var scrollEl = produktePanel.querySelector('theme-scroll');
        if (scrollEl) {
          scrollEl.innerHTML = newGrid + allLink;
        }
      }

      // Produkt-Count im Tab-Badge aktualisieren
      var badge = tmp.querySelector('.side-panel-content--tabs button:first-child .cb-search-tab-badge');
      if (badge) {
        var totalCards = 0;
        var finalGrid = tmp.querySelector('.search-drawer--products');
        if (finalGrid) {
          totalCards = finalGrid.querySelectorAll('.product-card, [data-product-id]').length;
        }
        if (totalCards > 0) badge.textContent = totalCards;
      }

      return tmp.querySelector('div').innerHTML;
    }

    /* ── Ergebnisse rendern ── */
    renderSearchResults(resultsMarkup) {
      var defaultContent = document.getElementById('cb-search-default');
      if (defaultContent) defaultContent.remove();

      this.predictiveSearchResults.innerHTML = resultsMarkup;
      this.predictiveSearchResults.classList.remove('loading');

      // Re-Init Tabs
      var tabsEl = this.predictiveSearchResults.querySelector('side-panel-content-tabs');
      if (tabsEl && typeof tabsEl.connectedCallback === 'function') {
        tabsEl.connectedCallback();
      }

      // GSAP Animations (falls vorhanden)
      if (typeof window.ScrollTrigger !== 'undefined' && typeof window.gsap !== 'undefined') {
        ScrollTrigger.batch(this.predictiveSearchResults.querySelectorAll('.animations-true .product-card--image-primary'), {
          start: 'top 90%',
          onEnter: (elements) => {
            gsap.to(elements, { scale: 1, opacity: 1, stagger: 0.15, ease: window.theme.settings.animation_easing });
          }
        });
      }
    }

    close() {
      this.container.classList.remove('active');
    }
  }
  customElements.define('search-form', SearchForm);
}